/**
 * Прогнозный ход судна на сутки: прямая от последнего фикса по курсу и
 * скорости, которые сообщил АИС.
 *
 * Модуль чистый — карты не касается. Вся арифметика и все отказы здесь.
 */
import { anchorLng } from './callout'

/** Горизонт прогноза, часы. Заказ владельца: сутки вперёд. */
import { runTo } from './rhumb'

export const FORECAST_HOURS = 24

/** Ниже этой скорости хода нет: линия выродилась бы в точку. */
export const MIN_SOG_KT = 0.5

/** Шаг отметок на линии, часы. */
export const MARK_EVERY_HOURS = 6

/** Операции, при которых судно маневрирует: прямая на сутки для них — фигура
 *  речи, а не обещание. Имена — те же, что шлёт бэкенд (`vesselOps.ts`). */
const MANOEUVRING = new Set(['searching', 'fishing', 'drifting'])

/** Курс по грунту приоритетнее того, куда смотрит нос: при сносе течением они
 *  расходятся, а на карте нас интересует, куда судно СМЕЩАЕТСЯ. Тот же порядок,
 *  что у маркера судна (`vesselMarkerStyle.ts`). */
function courseOf(fix: { cog: number | null; heading: number | null }): number | null {
  if (fix.cog != null && Number.isFinite(fix.cog)) return fix.cog
  if (fix.heading != null && Number.isFinite(fix.heading)) return fix.heading
  return null
}

export interface VesselFix {
  lat: number
  lon: number
  cog: number | null
  heading: number | null
  /** Скорость по грунту, УЗЛЫ. */
  sog: number | null
  fixUtc: string | null
  stale: boolean
  operation: string | null
}

export type ForecastResult =
  | { drawn: false; reasonKey: string }
  | {
      drawn: true
      line: GeoJSON.Feature<GeoJSON.LineString>
      marks: GeoJSON.Feature<GeoJSON.Point>[]
      /** Сплошная линия или мягкий пунктир. */
      confidence: 'firm' | 'soft'
      /** Ключ оговорки для карточки; `null` — оговаривать нечего. */
      noticeKey: string | null
    }

/** Доверие к прямой: сплошную получает только судно, про которое ИЗВЕСТНО, что
 *  оно на переходе. Незнание режима к уверенности не приравнивается — разметку
 *  операций кладёт часовой таймер, и у свежего фикса сегмента может не быть. */
function trustOf(operation: string | null): {
  confidence: 'firm' | 'soft'
  noticeKey: string | null
} {
  if (operation === 'steaming') return { confidence: 'firm', noticeKey: null }
  if (operation != null && MANOEUVRING.has(operation)) {
    return { confidence: 'soft', noticeKey: 'manoeuvring' }
  }
  return { confidence: 'soft', noticeKey: 'unknownMode' }
}

/** Метка времени отметки; без времени фикса её нет — подставить «сейчас»
 *  значило бы выдумать час, которого нам никто не сообщал. */
function markUtc(fixUtc: string | null, hours: number): string | null {
  if (!fixUtc) return null
  const started = Date.parse(fixUtc)
  if (!Number.isFinite(started)) return null
  return new Date(started + hours * 3600_000).toISOString()
}

export interface ForecastOptions {
  /** Долгота центра кадра. Приводит линию в ТОТ ЖЕ оборот мира, в котором стоит
   *  карта: в базе долгота знаковая (−179,5°), и за швом такая линия уехала бы
   *  на целый мир левее экрана. Тот же приём, что у выноски (`anchorLng`). */
  centerLng?: number
}

export function forecastRun(fix: VesselFix, opts: ForecastOptions = {}): ForecastResult {
  // Порядок отказов — от самого сильного к частному: устаревший фикс делает
  // бессмысленными и курс, и скорость, какими бы они ни были.
  if (fix.stale) return { drawn: false, reasonKey: 'staleFix' }
  const course = courseOf(fix)
  if (course == null) return { drawn: false, reasonKey: 'noCourse' }
  // Хода нет — линии нет. Спрашиваем именно СКОРОСТЬ, а не разметку: она
  // описывает недавнее прошлое, а фикс — настоящее, и они расходятся (замер
  // прода 20.08.2026: судно, размеченное `moored`, идёт 4,3 узла). Отказав по
  // разметке, мы написали бы «судно стоит» в карточке, которая тут же
  // показывает скорость, — карточка заспорила бы сама с собой.
  const sog = fix.sog ?? 0
  if (sog < MIN_SOG_KT) {
    // Причину называем режимом, когда он с фиксом СОГЛАСЕН: «судно у причала»
    // человек понимает, а «скорость ноль» заставляет догадываться.
    return { drawn: false, reasonKey: fix.operation === 'moored' ? 'moored' : 'notMoving' }
  }
  // Приводим СТАРТ, а дальше линия строится от него непрерывно: сместив каждую
  // точку порознь, мы вернули бы шов внутрь линии.
  const startLon = opts.centerLng == null ? fix.lon : anchorLng(fix.lon, opts.centerLng)
  const end = runTo(fix.lat, startLon, course, sog * FORECAST_HOURS)
  const trust = trustOf(fix.operation)

  const marks: GeoJSON.Feature<GeoJSON.Point>[] = []
  for (let h = MARK_EVERY_HOURS; h <= FORECAST_HOURS; h += MARK_EVERY_HOURS) {
    marks.push({
      type: 'Feature',
      properties: { hours: h, utc: markUtc(fix.fixUtc, h) },
      geometry: { type: 'Point', coordinates: runTo(fix.lat, startLon, course, sog * h) },
    })
  }

  return {
    drawn: true,
    line: {
      type: 'Feature',
      properties: { confidence: trust.confidence },
      geometry: { type: 'LineString', coordinates: [[startLon, fix.lat], end] },
    },
    marks,
    ...trust,
  }
}

/** Линия и отметки одной коллекцией для карты.
 *
 *  Доверие помечается на КАЖДОЙ фиче, а не только на линии: слои отбираются
 *  фильтром по `confidence`, и отметка без метки выпала бы из обоих слоёв —
 *  то есть просто не нарисовалась бы, ничего не сообщив.
 */
export function forecastCollection(
  res: Extract<ForecastResult, { drawn: true }>,
): GeoJSON.FeatureCollection {
  const stamp = (f: GeoJSON.Feature): GeoJSON.Feature => ({
    ...f,
    properties: { ...(f.properties ?? {}), confidence: res.confidence },
  })
  return {
    type: 'FeatureCollection',
    features: [stamp(res.line), ...res.marks.map(stamp)],
  }
}

/** Свойства фичи судна → фикс для прогноза.
 *
 *  Приведение мягкое и с уклоном В ОТКАЗ: битое число читается как «не знаем»,
 *  а не как ноль (ноль скорости означал бы «судно стоит» — вывод, которого нам
 *  никто не сообщал), а отсутствие признака свежести — как «позиция устарела».
 *  Прогноз есть утверждение о будущем, и ошибаться он обязан в сторону молчания.
 */
export function fixFromProps(
  props: Record<string, unknown>,
  anchor: { lng: number; lat: number },
): VesselFix {
  const n = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const s = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() !== '' ? v : null
  return {
    lat: anchor.lat,
    lon: anchor.lng,
    cog: n(props.cog),
    heading: n(props.heading),
    sog: n(props.sog),
    fixUtc: s(props.last_position_utc),
    stale: props.stale !== false,
    operation: s(props.operation),
  }
}
